import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  GiftIcon,
  TruckIcon,
  CheckCircleIcon,
  UserIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { ListPageSkeleton } from '../ui/PageSkeleton';
import EmptyState from '../ui/EmptyState';
import PageHeader from '../ui/PageHeader';
import Modal from '../ui/Modal';
import ProspectLevelStars from '../ui/ProspectLevelStars';
import Button from '../ui/Button';
import ActionOutcomePanel from '../ui/ActionOutcomePanel';
import ViewState from '../ui/ViewState';

function ClientGiftsInTransitView({ onPendingCountChange }) {
  const { t, formatMessage } = useLanguage();
  const g = t.giftsInTransit;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [actionOutcome, setActionOutcome] = useState(null);
  const [reviewModalClaim, setReviewModalClaim] = useState(null);
  const [productRating, setProductRating] = useState(0);
  const [vendorRating, setVendorRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const validation = t.ux?.validation || {};

  const fetchGifts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await axios.get('/api/client/gifts-in-transit', { withCredentials: true });
      setData(response.data);
    } catch (error) {
      console.error('Error loading gifts in transit:', error);
      setLoadError(g.failedToLoad);
      toast.error(g.failedToLoad);
    } finally {
      setLoading(false);
    }
  }, [g.failedToLoad]);

  useEffect(() => {
    fetchGifts();
  }, [fetchGifts]);

  const openReviewModal = (claim) => {
    setReviewModalClaim(claim);
    setProductRating(0);
    setVendorRating(0);
    setReviewText('');
  };

  const closeReviewModal = () => {
    if (submittingReview) return;
    setReviewModalClaim(null);
  };

  const handleConfirmReceipt = async () => {
    if (!reviewModalClaim) return;

    const errors = {};
    if (productRating < 1) errors.productRating = validation.ratingsRequired || g.ratingsRequired;
    if (vendorRating < 1) errors.vendorRating = validation.ratingsRequired || g.ratingsRequired;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmittingReview(true);
    setConfirmingId(reviewModalClaim.id);
    try {
      await axios.post(
        `/api/client/gifts-in-transit/${reviewModalClaim.id}/confirm`,
        {
          product_rating: productRating,
          vendor_rating: vendorRating,
          review_text: reviewText.trim(),
        },
        { withCredentials: true }
      );
      toast.success(g.confirmSuccess);
      const outcomeCopy = t.ux?.actionOutcome?.giftConfirmed;
      setActionOutcome({
        id: `confirm-${reviewModalClaim.id}-${Date.now()}`,
        title: outcomeCopy?.title || g.confirmSuccess,
        description: reviewModalClaim.reward_name,
        nextStep: outcomeCopy?.nextStep,
      });
      setReviewModalClaim(null);
      setFieldErrors({});
      await fetchGifts();
      onPendingCountChange?.();
    } catch (error) {
      toast.error(error.response?.data?.error || g.confirmFailed);
    } finally {
      setSubmittingReview(false);
      setConfirmingId(null);
    }
  };

  const renderClaimCard = (claim, { variant }) => {
    const isOnTheWay = variant === 'onTheWay';
    const isDelivered = variant === 'delivered';

    return (
      <motion.article
        key={claim.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-premium p-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <div className="icon-tile h-11 w-11 shrink-0 from-orange-500 to-amber-500">
              <GiftIcon className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-secondary-900 ">
                {claim.reward_name}
              </h3>
              {claim.reward_description ? (
                <p className="mt-1 text-sm text-secondary-600 ">
                  {claim.reward_description}
                </p>
              ) : null}
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-secondary-500 ">
                <span className="inline-flex items-center gap-1">
                  <UserIcon className="h-3.5 w-3.5" />
                  {claim.vendor_username || g.noVendor}
                </span>
                <span>{claim.points_spent} {g.pointsLabel}</span>
              </div>
              {isOnTheWay ? (
                <>
                  <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800  ">
                    <TruckIcon className="h-3.5 w-3.5" />
                    {claim.status === 'pending_admin' ? g.preparingBadge : g.onTheWayBadge}
                  </span>
                  <p className="mt-2 text-sm text-secondary-700 ">
                    {formatMessage(g.clientDeliveryPromise, {
                      days: claim.client_days_remaining ?? claim.client_delivery_days ?? 7,
                    })}
                  </p>
                </>
              ) : null}
              {isDelivered ? (
                <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-success-100 px-3 py-1 text-xs font-medium text-success-700  ">
                  <CheckCircleIcon className="h-3.5 w-3.5" />
                  {g.deliveredByVendorBadge}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {isDelivered && claim.can_client_confirm ? (
          <Button
            disabled={confirmingId === claim.id}
            loading={confirmingId === claim.id}
            onClick={() => openReviewModal(claim)}
            size="compact"
            className="w-full shrink-0 max-lg:min-h-[44px] lg:w-auto"
          >
            {g.confirmReceipt}
          </Button>
        ) : null}
      </motion.article>
    );
  };

  if (loading) {
    return <ListPageSkeleton rows={5} />;
  }

  if (loadError) {
    return (
      <div className="space-y-6 xl:space-y-7">
        <PageHeader title={g.title} subtitle={g.subtitle} icon={TruckIcon} />
        <ViewState error={loadError} onRetry={fetchGifts} />
      </div>
    );
  }

  const onTheWay = data?.on_the_way || [];
  const deliveredByVendor = data?.delivered_by_vendor || [];
  const hasAny = onTheWay.length > 0 || deliveredByVendor.length > 0;

  return (
    <div className="space-y-6 xl:space-y-7">
      <PageHeader
        title={g.title}
        subtitle={g.subtitle}
        icon={TruckIcon}
      />

      <ActionOutcomePanel outcome={actionOutcome} onDismiss={() => setActionOutcome(null)} />

      {!hasAny ? (
        <EmptyState
          icon={GiftIcon}
          title={g.emptyTitle}
          description={g.emptyDesc}
        />
      ) : (
        <>
          <section>
            <div className="mb-4 flex items-center gap-2">
              <ClockIcon className="h-5 w-5 text-orange-500" />
              <h2 className="text-lg font-semibold text-secondary-900 ">
                {g.onTheWaySection} ({data?.on_the_way_count ?? onTheWay.length})
              </h2>
            </div>
            {onTheWay.length > 0 ? (
              <div className="space-y-3">
                {onTheWay.map((claim) => renderClaimCard(claim, { variant: 'onTheWay' }))}
              </div>
            ) : (
              <p className="text-sm text-secondary-600 ">{g.noOnTheWay}</p>
            )}
          </section>

          <section>
            <div className="mb-4 flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-success-500" />
              <h2 className="text-lg font-semibold text-secondary-900 ">
                {g.deliveredSection} ({data?.delivered_by_vendor_count ?? deliveredByVendor.length})
              </h2>
            </div>
            <p className="mb-3 text-sm text-secondary-600 ">
              {g.deliveredSectionDesc}
            </p>
            {deliveredByVendor.length > 0 ? (
              <div className="space-y-3">
                {deliveredByVendor.map((claim) => renderClaimCard(claim, { variant: 'delivered' }))}
              </div>
            ) : (
              <p className="text-sm text-secondary-600 ">{g.noDelivered}</p>
            )}
          </section>
        </>
      )}

      <Modal
        isOpen={Boolean(reviewModalClaim)}
        onClose={closeReviewModal}
        title={formatMessage(g.reviewModalTitle, { name: reviewModalClaim?.reward_name || '' })}
        size="default"
        closeOnOverlayClick={!submittingReview}
      >
        <div className="space-y-5">
          <p className="text-sm text-secondary-600 ">{g.reviewModalDesc}</p>

          <div>
            <p className="mb-2 text-sm font-medium text-secondary-900 ">
              {g.productRatingLabel}
            </p>
            <ProspectLevelStars
              value={productRating}
              onChange={(value) => {
                setProductRating(value);
                if (fieldErrors.productRating) {
                  setFieldErrors((prev) => ({ ...prev, productRating: null }));
                }
              }}
              size="lg"
              centered
              ariaLabel={g.productRatingLabel}
            />
            {fieldErrors.productRating ? (
              <p role="alert" className="mt-2 text-xs font-medium text-error-600 ">
                {fieldErrors.productRating}
              </p>
            ) : null}
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-secondary-900 ">
              {g.vendorRatingLabel}
            </p>
            <ProspectLevelStars
              value={vendorRating}
              onChange={(value) => {
                setVendorRating(value);
                if (fieldErrors.vendorRating) {
                  setFieldErrors((prev) => ({ ...prev, vendorRating: null }));
                }
              }}
              size="lg"
              centered
              ariaLabel={g.vendorRatingLabel}
            />
            {fieldErrors.vendorRating ? (
              <p role="alert" className="mt-2 text-xs font-medium text-error-600 ">
                {fieldErrors.vendorRating}
              </p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="gift-review-text"
              className="mb-2 block text-sm font-medium text-secondary-900 "
            >
              {g.reviewTextLabel}
            </label>
            <textarea
              id="gift-review-text"
              rows={3}
              maxLength={500}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder={g.reviewTextPlaceholder}
              className="w-full rounded-xl border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30   "
            />
          </div>

          <div className="flex flex-col-reverse gap-2 max-lg:[&_button]:w-full lg:flex-row lg:justify-end lg:[&_button]:w-auto">
            <Button variant="secondary" onClick={closeReviewModal} disabled={submittingReview}>
              {g.cancel}
            </Button>
            <Button onClick={handleConfirmReceipt} disabled={submittingReview}>
              {submittingReview ? g.processing : g.submitReview}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ClientGiftsInTransitView;
